import React, { useMemo, useState } from "react";

import getSponsoredArticles, {
  ArticleSponsorId,
  ArticleSponsorType,
  SponsoredArticle,
} from "api/requests/getSponsoredArticles";
import { Action } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";

import ArticleCard from "components/botanic/ArticleCard";
import Carousel from "components/botanic/Carousel";

export const SponsoredArticlesCarousel = ({
  sponsorType,
  sponsorId,
  showSeeMoreCard,
  title = "Related articles",
  onCardClick,
  initialArticles,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  showSeeMoreCard?: boolean;
  sponsorType: ArticleSponsorType;
  sponsorId: ArticleSponsorId;
  initialArticles?: SponsoredArticle[];
  trackImpression?: {
    label: string;
    category: string;
    customDimensions: Record<string, string | number>;
  };
  onCardClick?: (articleId?: number) => void;
}) => {
  const { trackNonInteractionEvent } = useEventTracker();
  const [articles, setArticles] = useState(initialArticles);

  useMemo(() => {
    if (!articles) {
      getSponsoredArticles({ id: sponsorId, sponsorType }).then(setArticles);
    }
    if (props.trackImpression) {
      const { category, label, customDimensions } = props.trackImpression;
      trackNonInteractionEvent(
        category,
        Action.impression,
        label,
        customDimensions,
      );
    }
  }, []);

  if (!articles?.length) {
    return null;
  }

  return (
    <div {...props}>
      <Carousel
        title={title}
        seeMoreHref="/news"
        showSeeMoreCard={showSeeMoreCard}
      >
        {articles.map((article) => (
          <ArticleCard
            author={article.author}
            className="mb-md"
            date={article.date}
            href={article.link}
            imageProps={{ sizes: [750] }}
            imageUrl={article.image}
            key={article.id}
            onClick={() => onCardClick?.(article.id)}
            tags={article.tags}
            title={article.title}
          />
        ))}
      </Carousel>
    </div>
  );
};
